import { useState, useRef, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  QrCodeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { StockProduct, StockItem } from '../../types/inventory';

interface ScanResult {
  serial: string;
  ok: boolean;
  error?: string;
  item?: StockItem;
  timestamp: number;
}

interface ManualFields {
  serialNumber: string;
  [key: string]: string;
}

// Loose acceptance — twelve hex chars with any (or no) separators.
function looksLikeMac(input: string): boolean {
  const stripped = input.trim().replace(/[\s:-]/g, '');
  return /^[0-9a-fA-F]{12}$/.test(stripped);
}

export function ReceiveStockModal({
  product,
  onClose,
}: {
  product: StockProduct;
  onClose: (received: number) => void;
}) {
  const qc = useQueryClient();
  const [batchTag, setBatchTag] = useState('');
  const [batchTagLocked, setBatchTagLocked] = useState(false);
  const [results, setResults] = useState<ScanResult[]>([]);
  const [isManual, setIsManual] = useState(false);
  const [manualFields, setManualFields] = useState<ManualFields>({ serialNumber: '' });
  const [pendingCount, setPendingCount] = useState(0);
  const [nextMac, setNextMac] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);
  const macInputRef = useRef<HTMLInputElement>(null);
  const resultsEndRef = useRef<HTMLDivElement>(null);

  const receivedCount = results.filter(r => r.ok).length;
  const failedCount = results.filter(r => !r.ok).length;

  // Keep scan input focused for USB HID scanner
  useEffect(() => {
    if (!isManual) {
      const timer = setTimeout(() => scanInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isManual, pendingCount]);

  // Scroll results list to bottom on new entries
  useEffect(() => {
    resultsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [results.length]);

  const receiveMutation = useMutation({
    mutationFn: ({ rawScan, mac }: { rawScan: string; mac: string | null }) =>
      api.adminReceiveStockItem({
        product_id: product.id,
        raw_scan: rawScan,
        batch_tag: batchTag || null,
        wifi_mac_address: mac,
      }),
    onSuccess: (item) => {
      setResults(r => [...r, { serial: item.serialNumber, ok: true, item, timestamp: Date.now() }]);
      setPendingCount(c => c - 1);
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'items'] });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'stats'] });
    },
    onError: (err: any, vars: { rawScan: string; mac: string | null }) => {
      const code = err?.response?.data?.code;
      const msg = code === 'DUPLICATE'
        ? 'Already in stock'
        : code === 'DUPLICATE_MAC'
          ? 'MAC already used by another item'
          : err?.response?.data?.error ?? 'Failed to receive';
      // Try to extract serial for display
      let serial = vars.rawScan;
      if (product.intakeParser === 'soten_qr') {
        const m = vars.rawScan.match(/ID=([^,]+)/);
        if (m) serial = m[1];
      }
      setResults(r => [...r, { serial, ok: false, error: msg, timestamp: Date.now() }]);
      setPendingCount(c => c - 1);
    },
  });

  const manualReceiveMutation = useMutation({
    mutationFn: () => {
      const attrs: Record<string, string> = {};
      for (const [k, v] of Object.entries(manualFields)) {
        if (k !== 'serialNumber' && k !== 'wifiMacAddress' && v) attrs[k] = v as string;
      }
      return api.adminReceiveStockItem({
        product_id: product.id,
        serial_number: manualFields.serialNumber,
        batch_tag: batchTag || null,
        wifi_mac_address: manualFields.wifiMacAddress?.trim() || null,
        attributes: attrs,
      });
    },
    onSuccess: (item) => {
      setResults(r => [...r, { serial: item.serialNumber, ok: true, item, timestamp: Date.now() }]);
      setManualFields({ serialNumber: '' });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'items'] });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'stats'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code;
      const msg = code === 'DUPLICATE'
        ? 'Already in stock'
        : code === 'DUPLICATE_MAC'
          ? 'MAC already used by another item'
          : err?.response?.data?.error ?? 'Failed';
      setResults(r => [...r, { serial: manualFields.serialNumber, ok: false, error: msg, timestamp: Date.now() }]);
    },
  });

  const handleScan = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const raw = (e.target as HTMLInputElement).value.trim();
    if (!raw) return;

    // Block submit if a MAC was started but isn't valid — saves the operator
    // from a receive-then-edit loop.
    const macTrimmed = nextMac.trim();
    if (macTrimmed && !looksLikeMac(macTrimmed)) {
      macInputRef.current?.focus();
      macInputRef.current?.select();
      return;
    }

    (e.target as HTMLInputElement).value = '';
    if (!batchTagLocked && batchTag) setBatchTagLocked(true);

    setPendingCount(c => c + 1);
    receiveMutation.mutate({ rawScan: raw, mac: macTrimmed || null });
    setNextMac('');
  }, [batchTag, batchTagLocked, nextMac, receiveMutation]);

  const handleMacKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      scanInputRef.current?.focus();
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <QrCodeIcon className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Receive Stock</h2>
              <p className="text-xs text-slate-400">{product.name} · {product.sku}</p>
            </div>
          </div>
          <button onClick={() => onClose(receivedCount)} className="p-1 text-slate-400 hover:text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Batch Tag */}
        <div className="px-5 py-3 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-400 whitespace-nowrap">Batch / PO ref</label>
            <input
              value={batchTag}
              onChange={e => setBatchTag(e.target.value)}
              disabled={batchTagLocked}
              placeholder="e.g. Soten May 2026 batch 1"
              className="flex-1 px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none disabled:opacity-50"
            />
            {batchTagLocked && (
              <button
                onClick={() => setBatchTagLocked(false)}
                className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                edit
              </button>
            )}
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="px-5 py-2 border-b border-slate-700 shrink-0 flex items-center gap-2">
          <button
            onClick={() => setIsManual(false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${!isManual ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            <QrCodeIcon className="w-3.5 h-3.5" />
            Scanner
          </button>
          <button
            onClick={() => setIsManual(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${isManual ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
            Manual Entry
          </button>
        </div>

        {/* Scanner Input */}
        {!isManual && (
          <div className="px-5 py-4 border-b border-slate-700 shrink-0 space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                <WifiIcon className="w-3.5 h-3.5" />
                WiFi MAC Address (optional)
              </label>
              <input
                ref={macInputRef}
                value={nextMac}
                onChange={e => setNextMac(e.target.value)}
                onKeyDown={handleMacKey}
                placeholder="AA:BB:CC:DD:EE:FF — type/scan, then Tab to QR"
                className={`w-full px-3 py-2 bg-slate-900 border rounded-lg text-white text-sm font-mono focus:outline-none ${
                  nextMac && !looksLikeMac(nextMac)
                    ? 'border-red-500/50 focus:border-red-500'
                    : 'border-slate-600 focus:border-cyan-500'
                }`}
              />
            </div>
            <div className="relative">
              <QrCodeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                ref={scanInputRef}
                onKeyDown={handleScan}
                autoFocus
                placeholder="Ready to scan — point scanner here and pull the trigger"
                className="w-full pl-10 pr-4 py-3 bg-slate-900 border-2 border-cyan-500/30 focus:border-cyan-500 rounded-lg text-white text-sm focus:outline-none placeholder-slate-500 transition-colors"
              />
            </div>
            <p className="text-xs text-slate-500">Enter MAC first if you have it (it can be added later from the item record), then scan QR — both save together.</p>
          </div>
        )}

        {/* Manual Entry */}
        {isManual && (
          <div className="px-5 py-4 border-b border-slate-700 shrink-0 space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Serial Number / Factory ID *</label>
              <input
                value={manualFields.serialNumber}
                onChange={e => setManualFields(f => ({ ...f, serialNumber: e.target.value }))}
                placeholder="04597fd2b72090"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                <WifiIcon className="w-3.5 h-3.5" />
                WiFi MAC Address (optional)
              </label>
              <input
                value={manualFields.wifiMacAddress ?? ''}
                onChange={e => setManualFields(f => ({ ...f, wifiMacAddress: e.target.value }))}
                placeholder="AA:BB:CC:DD:EE:FF"
                className={`w-full px-3 py-2 bg-slate-700 border rounded-lg text-white text-sm font-mono focus:outline-none ${
                  manualFields.wifiMacAddress && !looksLikeMac(manualFields.wifiMacAddress)
                    ? 'border-red-500/50 focus:border-red-500'
                    : 'border-slate-600 focus:border-cyan-500'
                }`}
              />
            </div>
            {product.attributesSchema.map(field => (
              <div key={field.key}>
                <label className="block text-xs text-slate-400 mb-1">
                  {field.label}{field.required ? ' *' : ''}
                </label>
                <input
                  value={manualFields[field.key] ?? ''}
                  onChange={e => setManualFields(f => ({ ...f, [field.key]: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                />
              </div>
            ))}
            <button
              onClick={() => manualReceiveMutation.mutate()}
              disabled={!manualFields.serialNumber || manualReceiveMutation.isPending}
              className="w-full py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {manualReceiveMutation.isPending && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              Add to Stock
            </button>
          </div>
        )}

        {/* Results List */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5 min-h-[120px]">
          {results.length === 0 && pendingCount === 0 && (
            <p className="text-slate-500 text-sm text-center pt-6">Scanned units will appear here</p>
          )}
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-1">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              <span>Saving {pendingCount} scan{pendingCount > 1 ? 's' : ''}…</span>
            </div>
          )}
          {[...results].reverse().map((r, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                r.ok ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
              }`}
            >
              {r.ok ? (
                <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
              ) : (
                <ExclamationTriangleIcon className="w-4 h-4 text-red-400 shrink-0" />
              )}
              <span className="font-mono text-xs text-slate-300 flex-1 truncate">{r.serial}</span>
              {r.ok ? (
                <span className="text-xs text-green-400">Received</span>
              ) : (
                <span className="text-xs text-red-400">{r.error}</span>
              )}
            </div>
          ))}
          <div ref={resultsEndRef} />
        </div>

        {/* Footer Summary */}
        <div className="px-5 py-4 border-t border-slate-700 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-green-400 font-medium">{receivedCount} received</span>
            {failedCount > 0 && <span className="text-red-400">{failedCount} failed</span>}
          </div>
          <button
            onClick={() => onClose(receivedCount)}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
          >
            {receivedCount > 0 ? `Done — ${receivedCount} added` : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
